import { useState } from 'react';
import { api, openAuthenticatedFile } from '../../api/client.js';
import { Alert, Field, PageHeader, datum, fehlertext, useLoad } from '../shared/ui.js';

interface Version {
  id: string;
  versionLabel: string;
  status: 'draft' | 'active' | 'archived';
  createdAt: string;
  activatedAt: string | null;
  documentTitle: string;
  _count: { documents: number };
}
type Felder = Record<string, string | null>;
const TEXTFELDER: Array<[keyof Felder & string, string, boolean]> = [
  ['documentTitle', 'Dokumenttitel', false],
  ['introText', 'Einleitung', true],
  ['validityText', 'Gueltigkeitshinweis', true],
  ['deliveryTermsText', 'Lieferbedingung', true],
  ['packagingNoteText', 'Verpackungs- und Frachthinweis', true],
  ['termsReferenceText', 'AGB-Verweis', true],
  ['closingText', 'Schlusstext', true],
  ['footerNote', 'Fusszeile', false],
];
const FIRMENFELDER: Array<[string, string]> = [
  ['companyName', 'Firmierung'],
  ['companyAddress', 'Strasse'],
  ['companyPostcode', 'PLZ'],
  ['companyCity', 'Ort'],
  ['senderLine', 'Absenderzeile'],
  ['phone', 'Telefon'],
  ['fax', 'Fax'],
  ['email', 'E-Mail'],
  ['website', 'Website'],
];

/** Angebotsvorlagen: kopieren, bearbeiten, Vorschau, aktivieren. Nur Betreiber. */
export function TemplatesPage(): React.JSX.Element {
  const { data, error, reload } = useLoad<{ items: Version[] }>('/api/admin/templates');
  const [err, setErr] = useState<string | null>(null);
  const [bearbeite, setBearbeite] = useState<string | null>(null);

  async function kopieren(v: Version): Promise<void> {
    const label = window.prompt(
      'Bezeichnung der neuen Version:',
      `${v.versionLabel}-neu`,
    );
    if (!label) return;
    setErr(null);
    try {
      const r = await api.post<{ versionId: string }>(
        `/api/admin/templates/${v.id}/copy`,
        { versionLabel: label },
      );
      reload();
      setBearbeite(r.versionId);
    } catch (x) {
      setErr(fehlertext(x));
    }
  }
  async function aktivieren(v: Version): Promise<void> {
    if (
      !window.confirm(
        `Version ${v.versionLabel} aktivieren? Neue Angebote verwenden ab sofort diese Texte. Bereits ausgestellte Angebote und ihre PDFs bleiben unveraendert.`,
      )
    )
      return;
    setErr(null);
    try {
      await api.post(`/api/admin/templates/${v.id}/activate`);
      reload();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }

  return (
    <>
      <PageHeader
        title="Angebotsvorlagen"
        lead="Unternehmensdaten und Geschaeftstexte des Angebots-PDF. Aenderungen erfolgen ueber eine neue Version."
      />
      <Alert text={error ?? err} />
      {data && (
        <div className="card">
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Titel</th>
                  <th>Angelegt</th>
                  <th>Aktiviert</th>
                  <th className="num">Dokumente</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((v) => (
                  <tr key={v.id}>
                    <td>
                      <strong>{v.versionLabel}</strong>
                    </td>
                    <td>
                      {v.status === 'active' ? (
                        <span className="pill pill-ok">aktiv</span>
                      ) : v.status === 'draft' ? (
                        <span className="pill pill-warn">Entwurf</span>
                      ) : (
                        <span className="pill pill-off">archiviert</span>
                      )}
                    </td>
                    <td>{v.documentTitle}</td>
                    <td>{datum(v.createdAt)}</td>
                    <td>{datum(v.activatedAt)}</td>
                    <td className="num">{v._count.documents}</td>
                    <td className="row">
                      <button
                        type="button"
                        className="link"
                        onClick={() =>
                          void openAuthenticatedFile(
                            `/api/admin/templates/${v.id}/preview`,
                          )
                        }
                      >
                        Vorschau
                      </button>
                      {v.status === 'draft' && (
                        <button
                          type="button"
                          className="link"
                          onClick={() => setBearbeite(v.id)}
                        >
                          bearbeiten
                        </button>
                      )}
                      {v.status === 'draft' && (
                        <button
                          type="button"
                          className="primary"
                          onClick={() => void aktivieren(v)}
                        >
                          Aktivieren
                        </button>
                      )}
                      <button
                        type="button"
                        className="link"
                        onClick={() => void kopieren(v)}
                      >
                        kopieren
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="note">
            Aktive und archivierte Versionen sind unveraenderlich -- auf sie verweisen
            archivierte PDFs. Zum Aendern: kopieren, bearbeiten, aktivieren.
          </p>
        </div>
      )}
      {bearbeite && (
        <Bearbeiten
          id={bearbeite}
          onDone={() => {
            setBearbeite(null);
            reload();
          }}
        />
      )}
    </>
  );
}

function Bearbeiten({
  id,
  onDone,
}: {
  id: string;
  onDone: () => void;
}): React.JSX.Element {
  const { data, error } = useLoad<
    Felder & {
      legalNotes: Array<{ label: string; value: string }>;
      bankAccounts: Array<{ bankName: string; iban: string; bic: string }>;
    }
  >(`/api/admin/templates/${id}`);
  const [f, setF] = useState<Felder | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const werte = f ?? data;
  if (werte === null) return <Alert text={error} />;
  const set = (k: string, v: string) =>
    setF({ ...(f ?? (data as Felder)), [k]: v === '' ? null : v });

  async function speichern(): Promise<void> {
    setErr(null);
    const {
      id: _i,
      createdAt: _c,
      updatedAt: _u,
      activatedAt: _a,
      status: _s,
      templateId: _t,
      legalNotes: _l,
      bankAccounts: _b,
      ...rest
    } = werte as Record<string, unknown>;
    try {
      await api.patch(`/api/admin/templates/${id}`, rest);
      onDone();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }

  return (
    <section className="card highlight">
      <h3>Version {String(werte['versionLabel'])} bearbeiten</h3>
      <h4>Unternehmensdaten</h4>
      <div className="grid2">
        {FIRMENFELDER.map(([k, l]) => (
          <Field key={k} label={l}>
            <input
              value={String(werte[k] ?? '')}
              onChange={(e) => set(k, e.target.value)}
            />
          </Field>
        ))}
      </div>
      <h4>Geschaeftstexte</h4>
      <p className="note">
        Diese Texte erscheinen auf jedem neu ausgestellten Angebot. Die rechtliche
        Pruefung liegt beim Betreiber; STAR nimmt sie so, wie sie hier stehen.
      </p>
      {TEXTFELDER.map(([k, l, lang]) => (
        <Field key={k} label={l}>
          {lang ? (
            <textarea
              rows={3}
              value={String(werte[k] ?? '')}
              onChange={(e) => set(k, e.target.value)}
            />
          ) : (
            <input
              value={String(werte[k] ?? '')}
              onChange={(e) => set(k, e.target.value)}
            />
          )}
        </Field>
      ))}
      <p className="note">
        Register- und Bankangaben werden in V1 ueber den Seed gepflegt; die Struktur ist
        vorhanden (JSON-Felder), eine Maske folgt bei Bedarf.
      </p>
      <Alert text={err} />
      <div className="row">
        <button type="button" className="primary" onClick={() => void speichern()}>
          Speichern
        </button>
        <button type="button" onClick={onDone}>
          Schliessen
        </button>
      </div>
    </section>
  );
}
